import React, { useCallback, useEffect, useRef, useState } from 'react';
import CloseIcon from 'mdi-react/CloseIcon';
import SearchIcon from 'mdi-react/SearchIcon';
import { handleRequest } from '../../redux/services/APIService';

const DEFAULT_PET_AVATAR = `${process.env.PUBLIC_URL}/img/default-animal-black.png`;
const SEARCH_DEBOUNCE_MS = 300;

function parentDisplayName(parent) {
  if (!parent) {
    return '';
  }
  if (parent.name) {
    return parent.name;
  }
  return `${parent.firstName || ''} ${parent.lastName || ''}`.trim();
}

function petNameMatchesQuery(petName, query) {
  if (!query) {
    return false;
  }
  return String(petName || '')
    .toLowerCase()
    .includes(query.toLowerCase());
}

export default function PetSearchSelect({
  onSelectPet,
  autoFocus = false,
  disabled = false,
  placeholder = 'Search by Parent, Pet, or Mobile Number',
  inputName = 'addToBoardSearch',
  onFocus,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const inputRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }, []);

  const resetSearch = useCallback(() => {
    clearTimer();
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setIsLoading(false);
  }, [clearTimer]);

  const clearQuery = useCallback(() => {
    requestIdRef.current += 1;
    clearTimer();
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setIsLoading(false);
    requestAnimationFrame(() => {
      if (inputRef.current && typeof inputRef.current.focus === 'function') {
        inputRef.current.focus();
      }
    });
  }, [clearTimer]);

  const runSearch = useCallback(async (rawQuery) => {
    const search = rawQuery.trim();
    const requestId = ++requestIdRef.current;

    if (!search) {
      setResults([]);
      setIsOpen(false);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setIsOpen(true);
    try {
      const searchResponse = await handleRequest(
        'get',
        `parent/search?search=${encodeURIComponent(search)}`,
        true,
      );
      if (requestId !== requestIdRef.current) {
        return;
      }

      const parents = Array.isArray(searchResponse?.data)
        ? searchResponse.data
        : [];

      const rows = [];
      parents.forEach((parent) => {
        (parent.pets || []).forEach((pet) => {
          rows.push({
            petId: pet._id,
            petName: pet.name,
            petImage: pet.image,
            parentId: parent._id,
            parentName: parentDisplayName(parent),
            pet,
            parent,
          });
        });
      });

      const matchingPets = rows.filter((row) =>
        petNameMatchesQuery(row.petName, search),
      );
      const visibleRows = matchingPets.length ? matchingPets : rows;
      visibleRows.sort((a, b) => {
        const petCmp = String(a.petName).localeCompare(String(b.petName));
        if (petCmp !== 0) {
          return petCmp;
        }
        return String(a.parentName).localeCompare(String(b.parentName));
      });

      setResults(visibleRows);
      setIsOpen(true);
    } catch (error) {
      if (requestId !== requestIdRef.current) {
        return;
      }
      setResults([]);
      setIsOpen(true);
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    clearTimer();
    const search = query.trim();
    if (!search) {
      requestIdRef.current += 1;
      setResults([]);
      setIsOpen(false);
      setIsLoading(false);
      return undefined;
    }

    debounceRef.current = setTimeout(() => {
      runSearch(query);
    }, SEARCH_DEBOUNCE_MS);

    return clearTimer;
  }, [clearTimer, query, runSearch]);

  useEffect(() => {
    if (!autoFocus) {
      return undefined;
    }
    requestAnimationFrame(() => {
      if (inputRef.current && typeof inputRef.current.focus === 'function') {
        inputRef.current.focus();
      }
    });
    return undefined;
  }, [autoFocus]);

  useEffect(
    () => () => {
      clearTimer();
      requestIdRef.current += 1;
    },
    [clearTimer],
  );

  const handleSelect = (row) => {
    onSelectPet?.({
      pet: row.pet,
      parent: row.parent,
    });
    resetSearch();
  };

  const hasQuery = query.length > 0;

  return (
    <div className="pet-search-select">
      <div className="pet-search-select__field">
        <span className="pet-search-select__icon" aria-hidden="true">
          <SearchIcon size={16} />
        </span>
        <input
          ref={inputRef}
          className={
            'pet-search-select__input' +
            (hasQuery ? ' pet-search-select__input--clearable' : '')
          }
          name={inputName}
          type="text"
          value={query}
          disabled={disabled}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') {
              return;
            }
            if (isLoading || results.length !== 1) {
              return;
            }
            e.preventDefault();
            handleSelect(results[0]);
          }}
          onFocus={() => {
            onFocus?.();
            if (results.length || isLoading) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          autoComplete="off"
        />
        {hasQuery ? (
          <button
            type="button"
            className="pet-search-select__clear"
            aria-label="Clear search"
            onMouseDown={(e) => e.preventDefault()}
            onClick={clearQuery}
          >
            <CloseIcon size={14} />
          </button>
        ) : null}
      </div>
      {isOpen ? (
        <div className="pet-search-select__dropdown" role="listbox">
          {isLoading ? (
            <div className="pet-search-select__empty">Searching...</div>
          ) : results.length === 0 ? (
            <div className="pet-search-select__empty">No match found</div>
          ) : (
            results.map((row) => (
              <button
                type="button"
                key={`${row.parentId}-${row.petId}`}
                className="pet-search-select__row"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(row)}
              >
                <img
                  className="pet-search-select__avatar"
                  src={row.petImage || DEFAULT_PET_AVATAR}
                  alt=""
                />
                <span className="pet-search-select__names">
                  <span className="pet-search-select__pet-name">
                    {row.petName}
                  </span>
                  <span className="pet-search-select__separator">{' \u2014 '}</span>
                  <span className="pet-search-select__parent-name">
                    {row.parentName}
                  </span>
                </span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
